package es.cragma.fichajefacil.ui.i18n

enum class AppLanguage(val displayName: String, val flagEmoji: String) {
    SPANISH("Español", "🇪🇸"),
    ENGLISH("English", "🇬🇧")
}

data class Strings(
    // Header & Brand
    val appTitle: String,
    val appSubtitle: String,
    val developedBy: String,

    // Login Screen
    val loginTitle: String,
    val loginSubtitle: String,
    val emailLabel: String,
    val passwordLabel: String,
    val loginButton: String,
    val orContinueWith: String,
    val googleSignIn: String,
    val appleSignIn: String,
    val registerCompanyButton: String,
    val exploreDemoMode: String,
    val enterEmailError: String,
    val emailNotRegisteredError: String,

    // Company Registration
    val companyRegisterTitle: String,
    val companyRegisterSubtitle: String,
    val step1LegalData: String,
    val companyNameLabel: String,
    val cifLabel: String,
    val addressLabel: String,
    val cityLabel: String,
    val countryLabel: String,
    val selectInitialPlan: String,
    val step2AdminAccount: String,
    val adminAccountSubtitle: String,
    val adminNameLabel: String,
    val adminPhoneLabel: String,
    val adminEmailLabel: String,
    val adminPasswordLabel: String,
    val createCompanyButton: String,
    val backToLoginButton: String,
    val fillAllRequiredFields: String,

    // Mode Selection
    val welcomeUser: String,
    val selectModeSubtitle: String,
    val workerModeTitle: String,
    val workerModeDesc: String,
    val enterWorkerMode: String,
    val managementModeTitle: String,
    val managementModeDesc: String,
    val enterManagementMode: String,

    // Worker Screen
    val workShiftTitle: String,
    val statusNotStarted: String,
    val statusWorking: String,
    val statusOnBreak: String,
    val statusFinished: String,
    val notStartedMessage: String,
    val locationVerifiedTitle: String,
    val locationUnverifiedTitle: String,
    val locationVerifiedDesc: String,
    val locationUnverifiedDesc: String,
    val startShiftButton: String,
    val pauseBreakButton: String,
    val resumeBreakButton: String,
    val finishShiftButton: String,
    val timeWorkedToday: String,
    val timeOnBreak: String,
    val clockInLocationLabel: String,
    val currentBreakNotice: String,
    val shiftFinishedToday: String,
    val breakNormativeTitle: String,
    val breakNormativeText: String,
    val clockInHistoryTitle: String,
    val noHistoryRecords: String,
    val shiftInProgress: String,
    val netTimeLabel: String,
    val breaksLabel: String,

    // Management Dashboard
    val tabGlobalCompanies: String,
    val tabGlobalClockIns: String,
    val tabWorkforceStatus: String,
    val tabClockInRecords: String,
    val tabCompanySettings: String,
    val tabTeam: String,
    val tabTeamClockIns: String,
    val workerModeButton: String,
    val managementModeButton: String,

    // Workforce Management
    val workforceTitle: String,
    val activeEmployeesLabel: String,
    val createWorkerButton: String,
    val realTimeStatusTitle: String,
    val createWorkerModalTitle: String,
    val editWorkerModalTitle: String,
    val firstNameLabel: String,
    val lastNameLabel: String,
    val phoneLabel: String,
    val docTypeLabel: String,
    val docNumberLabel: String,
    val positionLabel: String,
    val departmentLabel: String,
    val assignedRoleLabel: String,
    val allowUnverifiedClockInOption: String,
    val statusActive: String,
    val statusTerminated: String,
    val terminateWorkerNotice: String,
    val saveWorkerButton: String,
    val saveChangesButton: String,
    val cancelButton: String,

    // Clock-in Supervision
    val totalClockInsLabel: String,
    val authenticatedLabel: String,
    val unverifiedLabel: String,
    val filterAll: String,
    val filterVerified: String,
    val filterUnverified: String,
    val noFilterRecords: String,

    // Company Settings
    val settingsTitle: String,
    val positionsTitle: String,
    val positionsDesc: String,
    val addPositionLabel: String,
    val departmentsTitle: String,
    val departmentsDesc: String,
    val addDepartmentLabel: String,
    val addButton: String,
    val locationPolicyTitle: String,
    val locationPolicyDesc: String,

    // Terminated Account
    val terminatedAccountTitle: String,
    val terminatedAccountSubtitle: String,
    val terminatedCertificatesNoticeTitle: String,
    val terminatedCertificatesNoticeText: String,

    // Demo Mode
    val demoModeBanner: String,
    val demoRestrictionTitle: String,
    val demoRestrictionText: String,
    val understoodButton: String
)

object LocalizedStrings {
    val Spanish = Strings(
        appTitle = "Fichaje Facil",
        appSubtitle = "Sistema Inteligente de Fichaje y Control Horario",
        developedBy = "Desarrollado por CRAGMA",

        loginTitle = "Iniciar Sesión",
        loginSubtitle = "Introduce tus credenciales para acceder a Fichaje Facil",
        emailLabel = "Correo Electrónico",
        passwordLabel = "Contraseña",
        loginButton = "Iniciar Sesión",
        orContinueWith = "o continúa con",
        googleSignIn = "Continuar con Google",
        appleSignIn = "Iniciar sesión con Apple",
        registerCompanyButton = "🏢 Registrar Nueva Empresa",
        exploreDemoMode = "👀 Explorar app en modo demostración (demo@cragma.es)",
        enterEmailError = "Por favor, introduce tu correo electrónico",
        emailNotRegisteredError = "El correo electrónico no está registrado en ninguna empresa. Ponte en contacto con tu Administrador o Recursos Humanos.",

        companyRegisterTitle = "Registro de Empresa",
        companyRegisterSubtitle = "Crea tu espacio de trabajo corporativo y la cuenta de Administrador de Empresa",
        step1LegalData = "1. Datos Legales de la Empresa",
        companyNameLabel = "Nombre Legal de la Empresa / Razón Social",
        cifLabel = "CIF / NIF de la Empresa",
        addressLabel = "Dirección Legal",
        cityLabel = "Ciudad / Municipio",
        countryLabel = "País",
        selectInitialPlan = "Selecciona Plan Inicial",
        step2AdminAccount = "2. Cuenta Administrador de Empresa",
        adminAccountSubtitle = "Esta cuenta tendrá acceso completo para configurar la empresa y añadir trabajadores.",
        adminNameLabel = "Nombre y Apellidos del Administrador",
        adminPhoneLabel = "Teléfono de Contacto",
        adminEmailLabel = "Correo Electrónico Corporativo",
        adminPasswordLabel = "Contraseña de Acceso",
        createCompanyButton = "Crear Empresa y Cuenta Admin",
        backToLoginButton = "← Volver al Inicio de Sesión",
        fillAllRequiredFields = "Por favor, completa todos los campos obligatorios.",

        welcomeUser = "👋 ¡Hola, {name}!",
        selectModeSubtitle = "Como {role}, puedes acceder en dos modos distintos. Selecciona la opción deseada para continuar:",
        workerModeTitle = "Entrar para Fichar (Trabajador)",
        workerModeDesc = "Registra tu inicio de jornada, pausas y consulta tus horas.",
        enterWorkerMode = "Acceder a Modo Fichar",
        managementModeTitle = "Entrar como Administración / Gestión",
        managementModeDesc = "Acceso a la gestión de empleados, supervisión de fichajes y panel de control.",
        enterManagementMode = "Acceder a Panel de Gestión",

        workShiftTitle = "Jornada Laboral",
        statusNotStarted = "Sin Iniciar",
        statusWorking = "🟢 Trabajando",
        statusOnBreak = "🟡 En Descanso",
        statusFinished = "🔴 Finalizado",
        notStartedMessage = "Aún no has iniciado tu jornada de hoy.",
        locationVerifiedTitle = "Ubicación Activada (Verificado)",
        locationUnverifiedTitle = "Ubicación Desactivada (No Verificado)",
        locationVerifiedDesc = "Tu fichaje se registrará como autenticado con ubicación.",
        locationUnverifiedDesc = "Sugerencia: Activa la ubicación para autenticar tu fichaje.",
        startShiftButton = "▶ INICIAR JORNADA",
        pauseBreakButton = "⏸ Pausa / Descanso",
        resumeBreakButton = "▶ Reanudar Jornada",
        finishShiftButton = "⏹ Finalizar Jornada",
        timeWorkedToday = "TIEMPO TRABAJADO HOY",
        timeOnBreak = "TIEMPO EN DESCANSO",
        clockInLocationLabel = "Ubicación fichaje:",
        currentBreakNotice = "🟡 Te encuentras actualmente en período de descanso. Haz clic en 'Reanudar' para volver al trabajo.",
        shiftFinishedToday = "Has finalizado tu jornada de hoy.",
        breakNormativeTitle = "Normativa de Descansos (España - Art. 34.4 ET)",
        breakNormativeText = "En jornadas continuas superiores a 6 horas, es obligatorio un descanso mínimo de 15 minutos. Salvo que el convenio colectivo establezca lo contrario, el tiempo de descanso no se considera tiempo de trabajo efectivo.",
        clockInHistoryTitle = "📋 Historial de Fichajes Recientes",
        noHistoryRecords = "No tienes fichajes registrados en el historial.",
        shiftInProgress = "Jornada en curso",
        netTimeLabel = "Tiempo neto",
        breaksLabel = "Pausas",

        tabGlobalCompanies = "🏢 Empresas Globales",
        tabGlobalClockIns = "📋 Fichajes Globales",
        tabWorkforceStatus = "👥 Plantilla y Estado",
        tabClockInRecords = "📋 Registro de Fichajes",
        tabCompanySettings = "⚙ Ajustes Empresa",
        tabTeam = "🎯 Mi Equipo",
        tabTeamClockIns = "📋 Fichajes del Equipo",
        workerModeButton = "⏱ Modo Fichar",
        managementModeButton = "📊 Gestión",

        workforceTitle = "Gestión de Plantilla & Personal",
        activeEmployeesLabel = "de {max} Empleados Activos",
        createWorkerButton = "+ Crear Trabajador",
        realTimeStatusTitle = "Estado y Datos de la Plantilla",
        createWorkerModalTitle = "Alta de Nuevo Trabajador",
        editWorkerModalTitle = "Editar / Dar de Baja",
        firstNameLabel = "Nombre",
        lastNameLabel = "Apellidos",
        phoneLabel = "Teléfono de Contacto",
        docTypeLabel = "Tipo Doc.",
        docNumberLabel = "Nº Documento",
        positionLabel = "Puesto de Trabajo",
        departmentLabel = "Departamento",
        assignedRoleLabel = "Rol Asignado en la Empresa:",
        allowUnverifiedClockInOption = "Permitir fichajes sin ubicación",
        statusActive = "Estado: Alta (Activo)",
        statusTerminated = "Estado: Dado de Baja (Inactivo)",
        terminateWorkerNotice = "El trabajador NO podrá iniciar sesión en la app.",
        saveWorkerButton = "Guardar Trabajador",
        saveChangesButton = "Guardar Cambios",
        cancelButton = "Cancelar",

        totalClockInsLabel = "Total Fichajes",
        authenticatedLabel = "Autenticados",
        unverifiedLabel = "No Verificados",
        filterAll = "Todos",
        filterVerified = "✓ Autenticados",
        filterUnverified = "⚠ No Verificados",
        noFilterRecords = "No se encontraron fichajes para el filtro seleccionado.",

        settingsTitle = "⚙ Configuración de Empresa",
        positionsTitle = "1. Puestos de Trabajo Configurados",
        positionsDesc = "Estos puestos estarán disponibles en los desplegables al dar de alta o editar trabajadores.",
        addPositionLabel = "Añadir Nuevo Puesto",
        departmentsTitle = "2. Departamentos de la Empresa",
        departmentsDesc = "Departamentos disponibles para estructurar la plantilla.",
        addDepartmentLabel = "Añadir Nuevo Departamento",
        addButton = "+ Añadir",
        locationPolicyTitle = "3. Política General de Fichaje",
        locationPolicyDesc = "Permitir fichajes sin ubicación por defecto",

        terminatedAccountTitle = "Usuario Dado de Baja",
        terminatedAccountSubtitle = "Tu cuenta de usuario consta actualmente como dada de baja en la plataforma y el acceso ha sido desactivado.",
        terminatedCertificatesNoticeTitle = "ℹ Informes y Certificados:",
        terminatedCertificatesNoticeText = "Si necesitas solicitar tus certificados de retenciones, histórico de registro de jornada o documentación de finiquito, ponte en contacto directo con el departamento de Recursos Humanos de la empresa.",

        demoModeBanner = "Modo Demostración (demo@cragma.es): Puedes probar a iniciar jornada, pausas y finalizar. Los fichajes se borrarán al salir.",
        demoRestrictionTitle = "🔒 Modo Demostración Lectura",
        demoRestrictionText = "Estás explorando Fichaje Facil en modo demostración con la cuenta demo@cragma.es.\n\nEn este modo puedes consultar las pantallas e interfaz de usuario, pero las acciones de fichaje o modificación de datos están restringidas. Para fichar o gestionar empresas reales, regístrate o inicia sesión con tu cuenta corporativa.",
        understoodButton = "Entendido"
    )

    val English = Strings(
        appTitle = "Fichaje Facil",
        appSubtitle = "Intelligent Time Tracking & Attendance System",
        developedBy = "Developed by CRAGMA",

        loginTitle = "Sign In",
        loginSubtitle = "Enter your credentials to access Fichaje Facil",
        emailLabel = "Email Address",
        passwordLabel = "Password",
        loginButton = "Sign In",
        orContinueWith = "or continue with",
        googleSignIn = "Continue with Google",
        appleSignIn = "Sign in with Apple",
        registerCompanyButton = "🏢 Register New Company",
        exploreDemoMode = "👀 Explore app in demo mode (demo@cragma.es)",
        enterEmailError = "Please enter your email address",
        emailNotRegisteredError = "Email address is not registered in any company. Please contact your Administrator or HR.",

        companyRegisterTitle = "Company Registration",
        companyRegisterSubtitle = "Create your corporate workspace and Company Administrator account",
        step1LegalData = "1. Company Legal Details",
        companyNameLabel = "Legal Company Name / Trade Name",
        cifLabel = "Tax ID / CIF / VAT Number",
        addressLabel = "Legal Address",
        cityLabel = "City / Municipality",
        countryLabel = "Country",
        selectInitialPlan = "Select Initial Plan",
        step2AdminAccount = "2. Company Admin Account",
        adminAccountSubtitle = "This account will have full access to configure the company and add workers.",
        adminNameLabel = "Administrator Full Name",
        adminPhoneLabel = "Contact Phone",
        adminEmailLabel = "Corporate Email Address",
        adminPasswordLabel = "Access Password",
        createCompanyButton = "Create Company & Admin Account",
        backToLoginButton = "← Back to Sign In",
        fillAllRequiredFields = "Please fill in all required fields.",

        welcomeUser = "👋 Welcome, {name}!",
        selectModeSubtitle = "As {role}, you can enter in two distinct modes. Select your preferred option to continue:",
        workerModeTitle = "Enter to Clock In (Worker)",
        workerModeDesc = "Clock in/out, record breaks, and view your worked hours.",
        enterWorkerMode = "Access Clock-In Mode",
        managementModeTitle = "Enter as Management / Admin",
        managementModeDesc = "Access employee management, clock-in supervision, and control panel.",
        enterManagementMode = "Access Management Panel",

        workShiftTitle = "Work Shift",
        statusNotStarted = "Not Started",
        statusWorking = "🟢 Working",
        statusOnBreak = "🟡 On Break",
        statusFinished = "🔴 Finished",
        notStartedMessage = "You haven't started your shift today.",
        locationVerifiedTitle = "Location Enabled (Verified)",
        locationUnverifiedTitle = "Location Disabled (Unverified)",
        locationVerifiedDesc = "Your clock-in will be authenticated with location.",
        locationUnverifiedDesc = "Tip: Enable location to authenticate your clock-in.",
        startShiftButton = "▶ START SHIFT",
        pauseBreakButton = "⏸ Pause / Break",
        resumeBreakButton = "▶ Resume Shift",
        finishShiftButton = "⏹ Finish Shift",
        timeWorkedToday = "TIME WORKED TODAY",
        timeOnBreak = "TIME ON BREAK",
        clockInLocationLabel = "Clock-in location:",
        currentBreakNotice = "🟡 You are currently on a break. Click 'Resume' to return to work.",
        shiftFinishedToday = "You have finished your shift today.",
        breakNormativeTitle = "Break Regulations (Spain - Art. 34.4 ET)",
        breakNormativeText = "In continuous shifts exceeding 6 hours, a minimum 15-minute break is mandatory. Unless specified otherwise by collective agreement, break time is not considered effective working time.",
        clockInHistoryTitle = "📋 Recent Clock-In History",
        noHistoryRecords = "You have no clock-in records in your history.",
        shiftInProgress = "Shift in progress",
        netTimeLabel = "Net time",
        breaksLabel = "Breaks",

        tabGlobalCompanies = "🏢 Global Companies",
        tabGlobalClockIns = "📋 Global Clock-Ins",
        tabWorkforceStatus = "👥 Workforce & Status",
        tabClockInRecords = "📋 Clock-In Records",
        tabCompanySettings = "⚙ Company Settings",
        tabTeam = "🎯 My Team",
        tabTeamClockIns = "📋 Team Clock-Ins",
        workerModeButton = "⏱ Clock-In Mode",
        managementModeButton = "📊 Management",

        workforceTitle = "Workforce & Staff Management",
        activeEmployeesLabel = "of {max} Active Employees",
        createWorkerButton = "+ Create Worker",
        realTimeStatusTitle = "Staff Real-Time Status & Details",
        createWorkerModalTitle = "Register New Worker",
        editWorkerModalTitle = "Edit / Terminate Worker",
        firstNameLabel = "First Name",
        lastNameLabel = "Last Name",
        phoneLabel = "Contact Phone",
        docTypeLabel = "Doc Type",
        docNumberLabel = "Doc Number",
        positionLabel = "Job Position",
        departmentLabel = "Department",
        assignedRoleLabel = "Assigned Company Role:",
        allowUnverifiedClockInOption = "Allow clock-ins without location",
        statusActive = "Status: Active",
        statusTerminated = "Status: Terminated (Inactive)",
        terminateWorkerNotice = "The worker will NOT be able to log into the app.",
        saveWorkerButton = "Save Worker",
        saveChangesButton = "Save Changes",
        cancelButton = "Cancel",

        totalClockInsLabel = "Total Clock-Ins",
        authenticatedLabel = "Authenticated",
        unverifiedLabel = "Unverified",
        filterAll = "All",
        filterVerified = "✓ Authenticated",
        filterUnverified = "⚠ Unverified",
        noFilterRecords = "No clock-in records found for the selected filter.",

        settingsTitle = "⚙ Company Settings",
        positionsTitle = "1. Configured Job Positions",
        positionsDesc = "These positions will appear in dropdown menus when creating or editing workers.",
        addPositionLabel = "Add New Position",
        departmentsTitle = "2. Company Departments",
        departmentsDesc = "Departments available for structuring your workforce.",
        addDepartmentLabel = "Add New Department",
        addButton = "+ Add",
        locationPolicyTitle = "3. General Clock-In Policy",
        locationPolicyDesc = "Allow clock-ins without location by default",

        terminatedAccountTitle = "Account Deactivated",
        terminatedAccountSubtitle = "Your user account is currently marked as deactivated in the platform and access has been disabled.",
        terminatedCertificatesNoticeTitle = "ℹ Reports & Certificates:",
        terminatedCertificatesNoticeText = "If you need tax certificates, work shift logs, or final settlement documents, please contact your company's Human Resources department directly.",

        demoModeBanner = "Demo Mode (demo@cragma.es): You can test clocking in, breaks, and finishing. Clock-ins will be cleared on logout.",
        demoRestrictionTitle = "🔒 Read-Only Demo Mode",
        demoRestrictionText = "You are exploring Fichaje Facil in demo mode using demo@cragma.es.\n\nIn this mode you can browse screens and UI layouts, but clock-in or data modification actions are restricted. To clock in or manage real companies, please register or sign in with your corporate account.",
        understoodButton = "Understood"
    )

    fun get(lang: AppLanguage): Strings = when (lang) {
        AppLanguage.SPANISH -> Spanish
        AppLanguage.ENGLISH -> English
    }
}
